import type { WarehouseComponents } from "@steelyard/api-client";
import { warehouse as warehouseClient, unwrap } from "../api";

/**
 * Every response type below is the generated one from `openapi/warehouse.json`. The document types
 * each enum as its snake_case wire string and each entity's properties as present (Warehouse's
 * `Program.cs`), so nothing here casts a response.
 */
type Schemas = WarehouseComponents["schemas"];

/**
 * Warehouse service calls (007-inventory). One client per service (constitution V) — this is the
 * same `warehouse.json`/`createWarehouseClient` `005` (packing) also uses, re-captured rather than
 * duplicated (research R1).
 *
 * `qty()` mirrors `ordering.ts`'s `money()`: the wire types every decimal as `number | string`
 * (`Format: double` doesn't stop the generator emitting the union), so this narrows it. It rounds
 * and computes nothing — no division against `conversionFactor`/`conversionToStocking`/a quantity
 * field anywhere in this file (FR-002, SC-004). A negative value passes through unchanged (FR-012);
 * there is no `Math.max(0, …)` here to remove.
 */
export function qty(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const QTY_FORMAT = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 3 });

/** Formatting, not calculation — renders whatever `qty()` narrowed to, negative sign included (FR-012). */
/** A person label the server could not resolve (erased account, Identity down) is `null` — shown as words, never as the id. */
export function personName(name: string | null | undefined): string {
  return name ?? "Unknown user";
}

/** "CODE — Name" from the API's own SKU labels; whichever half is missing is dropped, both missing is "—" (never the id). */
export function skuLabel(row: { skuCode?: string | null; skuName?: string | null }): string {
  return [row.skuCode, row.skuName].filter(Boolean).join(" — ") || "—";
}

export function formatQty(value: number | string | null | undefined): string {
  const parsed = qty(value);
  return parsed === null ? "—" : QTY_FORMAT.format(parsed);
}

export type DerivedCount = Schemas["DerivedCount"];

/** FR-001 to FR-004: on-hand, reserved and free, separately, plus the API's own derived counts. */
export type FreeStockView = Schemas["FreeStockView"];

/** O(1) from the projection (`InventoryController`'s own note) — never a movement scan. */
export async function getStock(skuId: string, warehouseId?: string) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/stock/{skuId}", {
      params: { path: { skuId }, query: { warehouseId } },
    }),
  );
}

/** "Show Out-of-Stock Warning": whether `FreeStockView.lowStock` is ever set, and below how many kg. */
export type StockWarningSettingsInput = Schemas["SetStockWarningSettingsRequest"];

/** Every SKU approaching out of stock, most urgent first — empty while the warning is off. Carries the setting too. */
export type LowStockView = Schemas["LowStockView"];

export async function getLowStock() {
  return unwrap((await warehouseClient()).GET("/api/warehouse/stock/low"));
}

/** Saves the setting; answers what is now in force. The stock screens apply it on their next read. */
export async function setStockWarningSettings(body: StockWarningSettingsInput) {
  return unwrap((await warehouseClient()).PUT("/api/warehouse/stock-warning-settings", { body }));
}

/** `MovementKind` (`MovementKind.cs`) — wire strings under `JsonStringEnumConverter(SnakeCaseLower)`, not the `number` `warehouse.d.ts` types them as (verified live, T024). */
export type MovementKind = Schemas["MovementKind"];
/** `MovementSource` — same wire-string mismatch as `MovementKind` above. */
export type MovementSource = Schemas["MovementSource"];

export type MovementView = Schemas["MovementView"];

export type MovementHistory = Schemas["MovementHistory"];

/** FR-014 — every movement's cause, named. Presentation only; the wire values themselves are the source of truth. */
export function movementKindLabel(kind: MovementKind): string {
  switch (kind) {
    case "receipt":
      return "Goods receipt";
    case "decrement":
      return "Packing decrement";
    case "adjustment":
      return "Adjustment";
    case "upload":
      return "Bulk upload";
    case "reversal":
      return "Reversal";
    case "reservation":
      return "Reservation";
  }
}

/** Titles a machine reason code ("count_correction") for display — the wire value itself is what's sent/stored (FR-010). Shared by Adjustments.tsx and the movement history table (T024). */
export function reasonCodeLabel(code: string): string {
  const words = code.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function movementSourceLabel(sourceKind: MovementSource): string {
  switch (sourceKind) {
    case "packing_completion":
      return "packing completion";
    case "goods_receipt":
      return "goods receipt";
    case "adjustment":
      return "adjustment";
    case "upload":
      return "stock upload";
    case "reservation":
      return "order reservation";
  }
}

/** "from goods receipt GRN · Supplier · date" — the movement's cause by the API's own `sourceLabel` (order number, GRN, upload file, adjustment reason); none sent leaves just the kind. */
export function movementSourceText(movement: { sourceKind: MovementSource; sourceLabel?: string | null }): string {
  return `from ${movementSourceLabel(movement.sourceKind)}${movement.sourceLabel ? ` ${movement.sourceLabel}` : ""}`;
}

/** FR-014/US5. `GET /stock/{skuId}/movements` — no pagination, no server-side `cause` filter (verified against `InventoryController.GetStockMovements`: only `skuId`/`warehouseId?`). */
export async function getStockMovements(skuId: string, warehouseId?: string) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/stock/{skuId}/movements", {
      params: { path: { skuId }, query: { warehouseId } },
    }),
  );
}

/**
 * FR-015/US5 — SKUs whose balance needs looking at. **Backend gap**: spec US5-AS3/FR-015 names
 * three categories (missing conversion factor, negative balance, stale reservation);
 * `StockService.ExceptionsAsync` (verified in source) filters only `QtyStockingUnit < 0 ||
 * FreeStock < 0` — no missing-conversion-factor or stale-reservation row exists on this endpoint
 * at all. Documented in contracts/backend-gaps.md rather than fabricated client-side (there is no
 * bulk stock list to scan for a missing conversion factor either — only `GET /stock/{skuId}`
 * per-id).
 */
export type StockExceptionRow = Schemas["StockExceptionRow"];

export type StockExceptions = Schemas["StockExceptions"];

export async function getStockExceptions(warehouseId?: string) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/stock/exceptions", {
      params: { query: { warehouseId } },
    }),
  );
}

/** FR-017/US5 — same movement rows as `getStockMovements`, over a period and/or across the whole warehouse. */
export type StockMovementRow = Schemas["StockMovementRow"];

export type StockMovementReportView = Schemas["StockMovementReportView"];

/** Server defaults `from`=`to`-30d when omitted; refuses (400) `from > to` or a range over `Warehouse:MaxReportPeriodDays` (verified live: 982-day request → 400 naming the setting). Neither is pre-validated here (FR-034/041 — it's live configuration). */
export async function getStockMovementReport(params: { warehouseId?: string; skuId?: string; from?: string; to?: string }) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/reports/stock-movement", { params: { query: params } }),
  );
}

/**
 * US2/T014–T016 — goods receipts (FR-016 to FR-022).
 *
 * `status`/`kind`/`sourceKind`-shaped enums on this service are wire strings, not the numbers
 * `warehouse.d.ts` types them as: verified live (`POST /goods-receipts` against the running
 * gateway, 2026-09-09) returned `"status":"confirmed"`, lower-snake-case, not `1`. The generated
 * client's `GoodsReceiptStatus: number` is wrong for this service (same mismatch existed on
 * `MovementView.kind`/`sourceKind` above — `"receipt"`/`"goods_receipt"` on the wire, not numbers;
 * fixed at T024, since US5 is what reads them). `GoodsReceipt.status` below is typed and compared
 * as the real wire string.
 */
export type GoodsReceiptLine = Schemas["GoodsReceiptLineResponse"];

export type GoodsReceipt = Schemas["GoodsReceiptResponse"];

export type GoodsReceiptLineInput = {
  skuId: string;
  unitCode: string | null;
  quantity: number;
  conversionToStocking: number | null;
  rate: number | null;
};

/**
 * There is no route in this app's reach that would ever hand back `"reversed"`:
 * `RecordGoodsReceipt` returns the freshly created receipt (always confirmed) and
 * `ReverseGoodsReceipt` returns the new compensating receipt (also confirmed) — the original's
 * flip to Reversed happens server-side but there is no `GET /goods-receipts/{id}` to observe it
 * with (backend-gaps addendum, plan.md). Kept as a three-way label anyway so a caller with a real
 * read route later doesn't need a second map.
 */
export function goodsReceiptStatusLabel(status: string): "Draft" | "Confirmed" | "Reversed" {
  return status === "draft" ? "Draft" : status === "reversed" ? "Reversed" : "Confirmed";
}

/**
 * 008-reporting-dashboards T009 — the dashboard's packing-variance tile, and (later)
 * `/reports/variance` (FR-008 to FR-011). `varianceRate` is events-over-lines-packed, **already a
 * fraction** (e.g. `0.05`, verified against `ReportQueries.Rate()`) and `null` when nothing was
 * packed in the period — a zero rate over no packing would be the most flattering wrong answer this
 * report could give, so it is never coerced to `0` here (FR-002/DA-10). Display only: `Intl.NumberFormat`
 * percent-formats the fraction the API returned, it does not compute one (constitution I). The
 * `byPacker`/`bySku`/`byReasonCode`/`trend`/`events` breakdowns belong to the report screen, not the
 * dashboard tile — this type carries them so one client type serves both, not because the tile reads
 * them. Verified live against the running gateway 2026-09-09 with no packing in the default 30-day
 * window: `{empty:true, emptyReason:"no_packing_in_period", linesPacked:0, varianceRate:null}`.
 */
/**
 * `Band` (`Band.cs`) and `ReasonCode` (`ReasonCode.cs`) — wire strings under the same
 * `JsonStringEnumConverter(SnakeCaseLower)` (`WarehouseJsonOptions.cs`, wired into every controller
 * response via `Program.cs`'s `AddJsonOptions`) as `MovementKind`/`MovementSource`/
 * `GoodsReceiptStatus` above, all three of which were confirmed live off this exact converter.
 * `warehouse.d.ts` types both as bare `number` (same generator gap). **Not independently observed
 * on the wire**: the shared dev database carries zero packing entries across all of 2026 (verified
 * live 2026-09-09 — `GET /reports/variance?from=2026-01-01&to=2026-09-09` and `GET
 * /oversight/variance` both answered `empty`/`[]`), so no live `Band`/`ReasonCode` value exists to
 * fetch. Typed from the converter + the enum members' own C# names read at source
 * (`Band.Green|Amber|Red`, `ReasonCode.RollingTolerance|ShortLengthOffcut|
 * PackingMaterialIncluded|PartialQuantityBalanceToFollow|ScaleError|WrongProductRepick|Other`) —
 * mechanistically the same case as the three confirmed enums, not assumed independently. Re-verify
 * against a real event row the day this database has one.
 */
export type Band = Schemas["Band"];
export type ReasonCode = Schemas["ReasonCode"];

/** One line per band, for the CSV export — the table cell uses `getStatusBadge` instead (colour + text, SC-009). */
export function bandLabel(band: Band): string {
  return band.charAt(0).toUpperCase() + band.slice(1);
}

const PERCENT = new Intl.NumberFormat("en-IN", { style: "percent", maximumFractionDigits: 1 });

/** Formatting, not calculation — renders the API's own ratio (FR-002/DA-10). `null`/unparseable render "—", never coerced to 0%. */
export function formatPercent(rate: number | string | null | undefined): string {
  if (rate === null || rate === undefined || rate === "") return "—";
  const parsed = typeof rate === "number" ? rate : Number(rate);
  return Number.isFinite(parsed) ? PERCENT.format(parsed) : "—";
}

/** 008 T015 — one packer's rate over the period (FR-002, DA-01). `null` `varianceRate` means nothing was packed, never coerced to 0. */
export type PackerVarianceRow = Schemas["PackerVarianceRow"];

/** One SKU's rate over the period (FR-001). */
export type SkuVarianceRow = Schemas["SkuVarianceRow"];

/** How often each reason was given (FR-001). */
export type ReasonCodeRow = Schemas["ReasonCodeRow"];

/** One week of the period (FR-003, SC-003). A week with no packing carries a null rate, not a zero. */
export type VarianceTrendPoint = Schemas["VarianceTrendPoint"];

/** One amber or red event — green is the denominator only, never a row (DA-02, FR-004). */
export type VarianceEventRow = Schemas["VarianceEventRow"];

export type VarianceReportView = Schemas["VarianceReportView"];

/** Tolerance-band parameters behind an overlap — display only, mirrors `ResolvedTolerance` on the wire. */
export type ResolvedTolerance = Schemas["ToleranceLabelled"];

/** A SKU that could not be checked for collision (no expected weight to compare) — listed, never dropped silently (FR-012 backend note). */
export type CollisionCandidate = Schemas["CollisionCandidateResponse"];

/** One pair of SKUs whose expected-weight tolerance bands overlap enough that a scale reading cannot tell them apart. */
export type CollisionPair = Schemas["CollisionPair"];

/**
 * 008-reporting-dashboards T019 (FR-012, D-29). A snapshot of the catalogue's tolerance-band
 * overlaps as it stands, not a period — `dateField` is always `"none"` (verified against
 * `"/api/warehouse/reports/collisions"`'s `query?: never` in warehouse.d.ts, and the backend's own
 * `008-reporting/contracts/reports.md`: "statements about the catalogue... as they stand, not about
 * a period"). No from/to param exists to send.
 */
export type CollisionReportView = Schemas["CollisionReportView"];

export async function getCollisionReport() {
  return unwrap((await warehouseClient()).GET("/api/warehouse/reports/collisions"));
}

/**
 * Server defaults to the trailing 30 days when `from`/`to` are omitted (verified live 2026-09-09:
 * `periodFrom`/`periodTo` came back exactly 30 days apart with no query string at all). Takes any
 * `from`/`to` including one predating go-live — verified live 2026-09-09 with `from=2020-01-01`:
 * a normal 200 with that period's (empty, in this database) data, not a 400 and not a
 * client-invented cutover floor (FR-009, §22 — there is none here to remove).
 */
export async function getVarianceReport(params?: { from?: string; to?: string }) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/reports/variance", { params: { query: params ?? {} } }),
  );
}

/** FR-016, FR-018, FR-020, D-21: no PO, a free-text supplier, one v1 warehouse (FR-034/D-22). */
export async function recordGoodsReceipt(input: {
  supplierName: string;
  grnDate: string;
  lines: GoodsReceiptLineInput[];
}) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/goods-receipts", {
      body: { warehouseId: null, supplierName: input.supplierName, grnDate: input.grnDate, lines: input.lines },
    }),
  );
}

/** FR-021, DA-06: a mandatory reason; the response is the new compensating receipt, not the original. */
export async function reverseGoodsReceipt(receiptId: string, reason: string) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/goods-receipts/{receiptId}/reverse", {
      params: { path: { receiptId } },
      body: { reason },
    }),
  );
}

/**
 * US3/T019 (FR-010) — a live, client-configurable list, not a hard-coded one. `ReasonCodesResponse`
 * is `{ codes: string[] }` (the controller's own comment says it was deliberately wrapped, not a
 * bare array, after a past bug), so the unwrap here is just reading `.codes` out of it.
 *
 * The `Set` is not a computation on business data — it collapses duplicate entries for display
 * (verified live against the running gateway, 2026-09-09: the seeded config answered
 * `["damage","count_correction","theft","other","damage","count_correction","theft","other"]`,
 * every code twice), which would otherwise put two identical `<option>`s with the same React key
 * in the reason `SelectField`. The set of allowed values is unchanged either way.
 */
export async function listAdjustmentReasonCodes(): Promise<string[]> {
  const response = await unwrap((await warehouseClient()).GET("/api/warehouse/adjustments/reason-codes"));
  return [...new Set(response.codes)];
}

/**
 * US3/T018 (FR-009 to FR-012). `warehouseId` is deliberately omitted from the request —
 * `WarehouseOrDefault` picks the single v1 warehouse server-side (FR-005/D-22); there is no
 * warehouse selector on this screen to source one from.
 *
 * Two distinct refusal shapes reach here and both render verbatim through `ErrorSurface`, never
 * paraphrased (constitution VI): a blank/whitespace reason is a 422
 * (`reason_code_required`, from the controller itself); a non-blank reason not on the current
 * list, or a zero `qtyDelta`, is a 409 (`reason_code_unknown` / `delta_required`, thrown by
 * `StockService.AdjustAsync` as a `WarehouseRuleViolationException`). Neither is special-cased
 * here — `unwrap`'s `ApiError` already carries the real problem document either way.
 *
 * A `qtyDelta` that takes stock negative is NOT refused — `StockAdjustment`'s own doc comment says
 * it "applies and is reported" (FR-025 scenario 5) — so this makes no client-side judgement about
 * the resulting balance.
 */
export async function createAdjustment(input: {
  skuId: string;
  qtyDelta: number;
  reasonCode: string;
  note: string | null;
}): Promise<StockAdjustment> {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/adjustments", {
      body: { skuId: input.skuId, warehouseId: null, qtyDelta: input.qtyDelta, reasonCode: input.reasonCode, note: input.note },
    }),
  );
}

export type StockAdjustment = Schemas["StockAdjustmentResponse"];

/**
 * Sets one SKU's on hand to `quantity` (zero marks it out of stock) — the Stock page's popup.
 * Warehouse works out the difference and records it as a `count_correction` adjustment.
 */
export async function setOnHand(input: { skuId: string; quantity: number; note: string }) {
  const { error, response } = await (await warehouseClient()).POST("/api/warehouse/adjustments/set-on-hand", {
    body: { skuId: input.skuId, warehouseId: null, quantity: input.quantity, note: input.note },
  });
  if (error !== undefined || !response.ok) throw error ?? new Error(`Warehouse answered ${response.status}`);
}

/**
 * US4/T021-T023 (FR-013, FR-028 to FR-033, DA-07) — the bulk stock upload: validate (a dry run,
 * writes nothing), then commit.
 *
 * There is no `GET` route for a staged upload anywhere in Warehouse (verified against
 * `InventoryController.cs`) — a validated upload can only be shown back to whoever just validated
 * it, in that browser. `pages/inventory/uploads/{List,Preview}.tsx` cache the response in an
 * in-memory `Map` (see `upload-cache.ts`) for that reason, not `sessionStorage` — eslint.config.mjs
 * bans Web Storage; nothing here does even that.
 */
export type UploadError = Schemas["UploadError"];

export type UploadValidation = Schemas["UploadValidation"];

export type UploadCommit = Schemas["UploadCommit"];

/** Presentation only — titles one of the three literal reason codes the backend's parser reports. Never a fourth: an unrecognised code (a future backend addition) falls back to itself rather than a fabricated label. */
export function uploadErrorReason(reason: string): string {
  switch (reason) {
    case "unknown_sku":
      return "Unknown SKU — no SKU with this code in the catalogue";
    case "invalid_quantity":
      return "Invalid quantity — not a number, in the stocking unit";
    case "malformed_row":
      return 'Malformed row — expected "skuCode,quantity"';
    default:
      return reason;
  }
}

/**
 * FR-028: writes no stock and no movement. `warehouseId` omitted picks the v1 warehouse server-side (FR-005/D-22), same as receipts/adjustments.
 * `fileHash`: the CSV, uploaded to Files first (016).
 */
export async function validateStockUpload(fileName: string, fileHash: string, warehouseId?: string | null) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/stock-uploads/validate", {
      // TODO(016 T080): remove cast after openapi regen
      body: { fileName, warehouseId: warehouseId ?? null, fileHash } as unknown as Schemas["StockUploadRequest"],
    }),
  );
}

/** FR-029, FR-033, DA-07: a second commit of the same content is refused (409) — rendered verbatim via `ErrorSurface`, never retried silently. */
export async function commitStockUpload(uploadId: string) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/stock-uploads/{uploadId}/commit", {
      params: { path: { uploadId } },
    }),
  );
}

/**
 * 005-packing-verification, admin-packing-oversight bucket (T053-T059) — the **operational**
 * `/oversight/*` surface (`contracts/packing-oversight.md`), gated on `packing.oversight` /
 * `packing.override` / `packing.rules.administer` (verified against `OversightController.cs`'s own
 * `[Authorize]` attributes, and present verbatim in `../Steelyard/config/role-matrix.json`'s
 * `warehouse_supervisor` row — unlike GAP-5's usual placeholder gap, these three codes already
 * exist on the live matrix). Deliberately separate from the `/reports/*` types above:
 * `008-reporting`'s `VarianceReportView`/`CollisionReportView` carry the report envelope (`empty`,
 * `producedAt`, `dateField`); `packing-oversight.md` §2/§3's `VarianceReport`/`CollisionReport` do
 * not — they are live-status reads, not period reports, and the generated schemas name them
 * distinctly (`PackerVariance`/`SkuVariance` vs this file's own `PackerVarianceRow`/`SkuVarianceRow`
 * above).
 */

/**
 * `PackingTaskStatus` (`PackingTaskStatus.cs`) — wire strings under the same `SnakeCaseLower`
 * converter as `Band`/`ReasonCode` above (`WarehouseJsonOptions.Configure`, wired into every
 * controller via `Program.cs`), matching `packing-oversight.md` §1's own `?status=` vocabulary and
 * `screen-routes.md` verbatim. `warehouse.d.ts` types the wire field as bare `number` — same
 * generator gap as every other enum in this file.
 */
export type PackingTaskStatus = Schemas["PackingTaskStatus"];

export function packingTaskStatusLabel(status: PackingTaskStatus): string {
  return status === "in_progress" ? "In progress" : status.charAt(0).toUpperCase() + status.slice(1);
}

/** `CustomerType` (`Warehouse.Domain.Packing.CustomerType.cs`: `B2b`/`B2c`) — same converter, wire `"b2b"`/`"b2c"`. Distinct from Customers' own `CustomerType` (`Company`/`Individual`, `services/customers.ts`) — two services, two vocabularies, both real. */
export type PackingCustomerType = Schemas["CustomerType"];

export type PackingTaskLineRow = Schemas["PackingLineResponse"];

/** One row of `GET /oversight/tasks` (T053, FR-002, FR-003). `lines`/`entries` are nullable on the wire (`PackingTask.lines?`); only line count is shown here — a full line breakdown belongs to a task-detail screen this bucket does not add (no such route in `screen-routes.md`). */
export type PackingTaskRow = Schemas["PackingTaskResponse"];

/** T053 — live packing status. No pagination on this route (verified: `TasksAsync` takes only `status?`), so this is a plain unpaged fetch, same shape as `getVarianceReport` above. */
export async function getOversightTasks(status?: PackingTaskStatus) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/oversight/tasks", { params: { query: { status } } }),
  );
}

/** T059 — FR-002. Assigns a task to a packer by their identity id, picked from the packer list (`List.tsx`); the response carries `assignedToName`. */
export async function assignPackingTask(taskId: string, packerId: string) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/oversight/tasks/{taskId}/assign", {
      params: { path: { taskId } },
      body: { packerId },
    }),
  );
}

/** T059 — FR-003, DA-08. Returns the task to the queue; entries already recorded stay and keep naming the packer who made them (server-side, nothing to do here). */
export async function unassignPackingTask(taskId: string) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/oversight/tasks/{taskId}/unassign", {
      params: { path: { taskId } },
    }),
  );
}

/** The order's packing task, or null when it has none. Warehouse has no task-by-order read (see `PackerMenu`). */
export async function packingTaskForOrder(orderId: string): Promise<PackingTaskRow | null> {
  return (await getOversightTasks()).find((task) => task.orderId === orderId) ?? null;
}

/**
 * One line's reading as a Super Admin enters it on the web. `band` is what Warehouse last said of
 * it — green until a refusal says amber or red — so it travels as the band the person was shown,
 * exactly as the app's own is; Warehouse judges the reading itself (BR-05) and nothing here does.
 */
export type PackReading = {
  orderLineId: string;
  clientEntryId: string;
  actualWeight: number | null;
  actualPieceCount: number | null;
  actualDimension: number | null;
  standardSizeConfirmed: boolean;
  band: Band;
  reasonCode: ReasonCode;
  reasonNote: string | null;
  overrideReason: string | null;
};

export type SyncOutcome = Schemas["SyncOutcome"];

/**
 * The warehouse app's own sync (`POST /device/entries`), so a web pack is judged, recorded and
 * stocked like any other. The caller confirms the SKU and, on red, overrides as the supervisor —
 * Warehouse asks Identity whether they hold `packing.override`. A stable `clientEntryId` per line
 * makes a resend of an accepted reading a duplicate rather than a second entry.
 */
export async function syncPackReadings(taskId: string, actorId: string, readings: PackReading[]): Promise<SyncOutcome> {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/device/entries", {
      body: {
        deviceId: "admin-web",
        entries: readings.map((r) => ({
          clientEntryId: r.clientEntryId,
          packingTaskId: taskId,
          orderLineId: r.orderLineId,
          actualWeight: r.actualWeight,
          actualPieceCount: r.actualPieceCount,
          actualDimension: r.actualDimension,
          deviceBand: r.band,
          // No rule was evaluated on this side, so there is no version to report.
          deviceRuleVersion: 0,
          captureSource: "manual" as const,
          reasonCode: r.band === "amber" ? r.reasonCode : null,
          reasonNote: r.band === "amber" ? r.reasonNote : null,
          confirmedBy: actorId,
          collisionForced: false,
          capturedAt: new Date().toISOString(),
          supervisorOverride: r.band === "red" ? { supervisorId: actorId, reason: r.overrideReason } : null,
          standardSizeConfirmed: r.standardSizeConfirmed,
          requestSupervisorOverride: false,
        })),
      },
    }),
  ) as Promise<SyncOutcome>;
}

/** Completes the pack: stock falls by the actual quantities and Ordering hears the order is packed (BR-05). */
export async function completePackingTask(taskId: string) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/device/tasks/{taskId}/complete", { params: { path: { taskId } } }),
  );
}

/** One packer's rate over the period — `packing-oversight.md` §2, distinct from `PackerVarianceRow` above (that one is `008`'s report envelope; `varianceRate` there is nullable, here it is not — verified against the schema). */
export type OversightPackerVariance = Schemas["PackerVariance"];

/** One SKU's rate over the period — `packing-oversight.md` §2. */
export type OversightSkuVariance = Schemas["SkuVariance"];

export type OversightVarianceReport = Schemas["VarianceReport"];

/** T054 — FR-032, §12.6. Defaults to the last 30 days server-side when `from`/`to` are omitted (same default as `getVarianceReport`, verified against `OversightController.GetVariance`). */
export async function getOversightVariance(params?: { from?: string; to?: string }) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/oversight/variance", { params: { query: params ?? {} } }),
  );
}

/** `packing-oversight.md` §3 — the live collision snapshot, distinct from `CollisionReportView` above (no `empty`/`producedAt`/`forcedConfirmationStatement` envelope; `pairs`/`excluded`/`forcedConfirmationSkus` reuse this file's own `CollisionPair`/`CollisionCandidate` types verbatim — same schemas on the wire). */
export type OversightCollisionReport = Schemas["CollisionReportResponse"];

/** T057 — FR-034, D-29. Recomputed on every read server-side (research R4) — never cached or diffed here. */
export async function getOversightCollisions() {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/oversight/collisions"),
  );
}

/** `SpotCheckStatus` (`SpotCheck.cs`) — wire strings, same converter. Matches `packing-oversight.md` §4's `?status=` vocabulary. */
export type SpotCheckStatus = Schemas["SpotCheckStatus"];

export function spotCheckStatusLabel(status: SpotCheckStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** T056 — FR-040 to FR-042, D-30. `reweighedValue`/`discrepancy` are null until a supervisor answers it. */
export type SpotCheckRow = Schemas["SpotCheckResponse"];

export async function getOversightSpotChecks(status?: SpotCheckStatus) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/oversight/spot-checks", { params: { query: { status } } }),
  );
}

/**
 * T056 — FR-042. **`Warehouse:SpotCheckPercentage` itself has no HTTP route anywhere in this
 * service** (verified against `OversightController.cs` and `WarehouseOptions.cs`: it is
 * `IOptionsMonitor`-only server configuration, read at the point of use with no admin-facing
 * GET/PUT to change it over the wire). "Configure the percentage" from this screen is a real,
 * confirmed backend gap, not something this function fakes — there is no `configureSpotCheckRate`
 * call here to remove. What T056 **can** do — list the selections and record a re-weigh — is real.
 */
export async function reweighSpotCheck(spotCheckId: string, reweighedValue: number) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/oversight/spot-checks/{spotCheckId}/reweigh", {
      params: { path: { spotCheckId } },
      body: { reweighedValue },
    }),
  );
}

/** `ToleranceScope` (`ToleranceScope.cs`) — wire strings, same converter. Declaration order is FR-009's resolution order (most specific first), per the enum's own doc comment. */
export type ToleranceScope = Schemas["ToleranceScope"];

export function toleranceScopeLabel(scope: ToleranceScope): string {
  return scope.charAt(0).toUpperCase() + scope.slice(1);
}

/** T058 — FR-008 to FR-012. A change supersedes rather than edits: `PUT` returns a *new* row at `version + 1`, and the superseded row stays in history under its own id (verified against `ToleranceRule.Supersede`) — never mutated here. */
export type ToleranceRuleRow = Schemas["ToleranceRuleResponse"];

/** `includeSuperseded` shows the full version history (FR-013's audit trail); omitted, the server returns only standing rules. */
export async function getToleranceRules(includeSuperseded?: boolean) {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/oversight/tolerance-rules", { params: { query: { includeSuperseded } } }),
  );
}

export type ToleranceRuleValues = {
  minVariationPct: number;
  maxVariationPct: number;
  hardLimitPct: number;
  absoluteFloorKg: number;
};

/**
 * T058 — a new rule at a scope that has none. No business-rule validation happens here
 * (`tolerance_negative`, `tolerance_hard_limit_too_low`, `tolerance_scope_mismatch` are the API's
 * own refusals — constitution I: the UI submits what was entered and renders the server's verbatim
 * rejection, it does not pre-judge a tolerance value).
 */
export async function createToleranceRule(input: { scope: ToleranceScope; scopeId: string | null } & ToleranceRuleValues) {
  return unwrap(
    (await warehouseClient()).POST("/api/warehouse/oversight/tolerance-rules", { body: input }),
  );
}

/** T058 — supersedes the standing row at `ruleId`; the response is the new version, not the one requested (see `ToleranceRuleRow`'s own note). */
export async function updateToleranceRule(ruleId: string, input: ToleranceRuleValues) {
  return unwrap(
    (await warehouseClient()).PUT("/api/warehouse/oversight/tolerance-rules/{ruleId}", {
      params: { path: { ruleId } },
      body: input,
    }),
  );
}

/** T055 — FR-022, FR-023, BR-08. Separate from the variance queue: an override is a supervisor's assertion, an entry is a packer's (data-model.md §3). */
export type SupervisorOverrideRow = Schemas["SupervisorOverrideResponse"];

export async function getOversightOverrides() {
  return unwrap(
    (await warehouseClient()).GET("/api/warehouse/oversight/overrides"),
  );
}
